import React, { useCallback, useState } from 'react'

export interface FadeInImageProps
  extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'onLoad'> {
  /** Fade duration. 200ms is long enough to read as a reveal, short enough not to feel slow. */
  fadeMs?: number
}

/**
 * A product photo that RESOLVES instead of popping.
 *
 * ## The problem it fixes
 *
 * The certificates list renders one bare `<img src>` per card. Each request
 * finishes at its own moment, so a nine-card list paints its photos in nine
 * unrelated flashes — the list assembles itself in front of you rather than
 * arriving. Nothing was wrong with the layout (the boxes are already sized and
 * nothing shifts); the defect is purely that each photo's arrival is a hard
 * cut on a white box.
 *
 * A 200ms opacity ramp turns each cut into a reveal. Because the container is
 * already the right size and already carries a neutral fill, the photo fades up
 * over its own placeholder and the list reads as one surface settling.
 *
 * ## Why NOT a staggered card entrance
 *
 * The obvious alternative — animate the CARDS in, one after another — was
 * rejected. The cards' text is not late; it arrives in a single query and is
 * ready on the first paint. Staggering the cards would delay content that is
 * already there in order to decorate content that is not. The lateness is at
 * the image layer, so the reveal belongs at the image layer.
 *
 * It also stays consistent with the detail page, where sections arriving at
 * different times is the bug rather than the effect.
 *
 * ## Cached images do not fade
 *
 * `loaded` initialises from `img.complete` inside a callback ref, so an image
 * already in the browser cache mounts opaque with no transition at all. Fading
 * a cached image in would ADD a flash on every revisit, which is the opposite
 * of the point — the ramp is only ever spent on a photo the user genuinely had
 * to wait for.
 *
 * Geometry is passed straight through via `style`/`className`. That is
 * deliberate: the list card's img is absolutely positioned and out of flow
 * (see CertificatesTeaser — an in-flow img reported its intrinsic height to the
 * flex layout and made portrait photos grow the card), and that arrangement is
 * hard-won. This wraps the reveal around it without touching it.
 */
export const FadeInImage: React.FC<FadeInImageProps> = ({
  fadeMs = 200,
  style,
  onError,
  ...props
}) => {
  const [loaded, setLoaded] = useState(false)

  const ref = useCallback((node: HTMLImageElement | null) => {
    // `complete` is true for a cache hit AND for an image that finished between
    // render and this ref firing, so it closes the race that a bare onLoad
    // handler leaves open — a fast image can land before React attaches, and
    // that photo would then sit at opacity 0 forever.
    if (node?.complete) setLoaded(true)
  }, [])

  return (
    <img
      {...props}
      ref={ref}
      onLoad={() => setLoaded(true)}
      /* A broken or missing src must not leave an invisible element behind.
         VRG-323: the caller's own `onError` is CHAINED rather than replaced.
         Before this it was silently dropped — `onError` arrived inside `...props`
         and was overwritten by the handler below — so a caller could never learn
         that an image had failed. The internal reveal still runs first, so the
         existing behaviour is unchanged for every call site that passes nothing;
         a caller that DOES pass one can now swap in its own fallback (see
         WhereToScanPanel, which falls back to a glyph). */
      onError={e => {
        setLoaded(true)
        onError?.(e)
      }}
      decoding='async'
      style={{
        ...style,
        opacity: loaded ? 1 : 0,
        transition: `opacity ${fadeMs}ms ease-out`,
      }}
    />
  )
}

export default FadeInImage
